import type { ReportsBucket } from 'shared';

/** How a report's period is chosen. */
export type PeriodPreset = '7d' | '30d' | '90d' | 'school-year' | 'custom';

export const PERIOD_PRESETS: ReadonlyArray<{ value: PeriodPreset; label: string }> = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'school-year', label: 'Ano letivo' },
  { value: 'custom', label: 'Personalizado' },
];

export const DEFAULT_PRESET: PeriodPreset = '30d';

/** The school year starts on 1 September. */
const SCHOOL_YEAR_FIRST_MONTH = 8;

const PRESET_DAYS: Partial<Record<PeriodPreset, number>> = { '7d': 7, '30d': 30, '90d': 90 };

export interface DayRange {
  /** "YYYY-MM-DD", both days included. */
  from: string;
  to: string;
}

export function isPeriodPreset(value: string | null): value is PeriodPreset {
  return PERIOD_PRESETS.some((p) => p.value === value);
}

/** A date as the day it is here, "YYYY-MM-DD": never the day it is in UTC. */
export function toDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The date of a "YYYY-MM-DD" day, at midday: a day has no time, and midday
 * is never moved to another day by a change of the clocks. Null when the text
 * is not a day that exists.
 */
export function parseDay(day: string): Date | null {
  const match = DAY_PATTERN.exec(day);
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(year, month - 1, date, 12);
  const exists =
    parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === date;
  return exists ? parsed : null;
}

function daysBefore(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - days, 12);
}

/** The days a preset covers, today included. Null for a custom period. */
export function presetRange(preset: PeriodPreset, today: Date): DayRange | null {
  if (preset === 'custom') return null;
  if (preset === 'school-year') {
    const year = today.getMonth() >= SCHOOL_YEAR_FIRST_MONTH ? today.getFullYear() : today.getFullYear() - 1;
    return { from: toDay(new Date(year, SCHOOL_YEAR_FIRST_MONTH, 1, 12)), to: toDay(today) };
  }
  const days = PRESET_DAYS[preset] ?? 30;
  return { from: toDay(daysBefore(today, days - 1)), to: toDay(today) };
}

/** How many days a range covers, both ends included. Zero when it is not one. */
export function rangeDays(range: DayRange): number {
  const from = parseDay(range.from);
  const to = parseDay(range.to);
  if (!from || !to) return 0;
  return Math.round(Math.abs(to.getTime() - from.getTime()) / 86_400_000) + 1;
}

const shortDay = new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'short' });
const longDay = new Intl.DateTimeFormat('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' });
const fullDay = new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' });
const shortMonth = new Intl.DateTimeFormat('pt-PT', { month: 'short' });
const longMonth = new Intl.DateTimeFormat('pt-PT', { month: 'long', year: 'numeric' });

/** A day as it is written here, "15/06/2026". */
export function formatDay(day: string): string {
  const date = parseDay(day);
  return date ? fullDay.format(date) : day;
}

/** "01/09/2026 a 29/09/2026", or the one day when both are the same. */
export function formatRange(range: DayRange): string {
  if (range.from === range.to) return formatDay(range.from);
  return `${formatDay(range.from)} a ${formatDay(range.to)}`;
}

/** What goes under a column: short enough to fit. */
export function stepLabel(start: string, bucket: ReportsBucket): string {
  const date = parseDay(start);
  if (!date) return start;
  // Month and year are put together by hand: asked for both, the locale
  // answers with numbers alone, "06/26".
  if (bucket === 'month') return `${shortMonth.format(date)} ${String(date.getFullYear()).slice(2)}`;
  return shortDay.format(date);
}

/** What names a step in full, in a tooltip or a table. */
export function stepTitle(start: string, bucket: ReportsBucket): string {
  const date = parseDay(start);
  if (!date) return start;
  if (bucket === 'month') return longMonth.format(date);
  if (bucket === 'week') return `Semana de ${shortDay.format(date)}`;
  return longDay.format(date);
}

export const BUCKET_LABELS: Record<ReportsBucket, string> = {
  day: 'por dia',
  week: 'por semana',
  month: 'por mês',
};
